import { CheckCircle2, XCircle, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { useScraperSettings } from "@/hooks/useScraperSettings";
import { useMemo } from "react";
import type { SearchResult } from "@/types/search";

interface MobileFilterDrawerProps {
  selectedPlatforms: string[];
  hideOutOfStock: boolean;
  selectedBrands: string[];
  availableResults: SearchResult[];
  onPlatformToggle: (platformId: string) => void;
  onHideOutOfStockToggle: () => void;
  onBrandToggle: (brand: string) => void;
  onSelectAllBrands: () => void;
  onDeselectAllBrands: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const MobileFilterDrawer = ({
  selectedPlatforms,
  hideOutOfStock,
  selectedBrands,
  availableResults,
  onPlatformToggle,
  onHideOutOfStockToggle,
  onBrandToggle,
  onSelectAllBrands,
  onDeselectAllBrands,
  onSelectAll,
  onDeselectAll,
  isOpen,
  onOpenChange,
}: MobileFilterDrawerProps) => {
  const { scrapers, isLoading: loading } = useScraperSettings();
  
  const uniqueBrands = useMemo(() => {
    const brands = new Set<string>();
    availableResults.forEach(result => {
      if (result.brand && result.brand.trim() !== "") {
        brands.add(result.brand.trim());
      }
    });
    return Array.from(brands).sort();
  }, [availableResults]);
  
  const enabledPlatforms = scrapers.filter(s => s.enabled);
  const activeFiltersCount = 
    (hideOutOfStock ? 1 : 0) + 
    (selectedPlatforms.length < enabledPlatforms.length ? 1 : 0);
  
  const allSelected = enabledPlatforms.length > 0 && 
    enabledPlatforms.every(s => selectedPlatforms.includes(s.scraper_name));
  
  const allBrandsSelected = uniqueBrands.length > 0 && 
    uniqueBrands.every(b => selectedBrands.includes(b));

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader className="border-b">
          <DrawerTitle className="text-xl font-bold">Filtres de recherche</DrawerTitle>
          <DrawerDescription>
            Personnalisez vos résultats en {selectedPlatforms.length} plateforme{selectedPlatforms.length > 1 ? 's' : ''}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-4 py-6 space-y-8 overflow-y-auto max-h-[65vh]">
          {/* Stock Filter */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-bold flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Disponibilité
              </h4>
            </div>
            <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-xl border-2 border-border">
              <Label htmlFor="hide-stock" className="text-sm font-medium cursor-pointer flex-1">
                Masquer les ruptures de stock
              </Label>
              <Switch
                id="hide-stock"
                checked={hideOutOfStock}
                onCheckedChange={onHideOutOfStockToggle}
                className="data-[state=checked]:bg-primary"
              />
            </div>
          </div>

          {/* Platform Filter */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-bold">
                Plateformes ({selectedPlatforms.length}/{enabledPlatforms.length})
              </h4>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onSelectAll}
                  disabled={allSelected || loading}
                  className="text-xs h-8"
                >
                  Tout
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={onDeselectAll}
                  disabled={selectedPlatforms.length === 0 || loading}
                  className="text-xs h-8"
                >
                  Aucun
                </Button>
              </div>
            </div>
            
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Chargement des plateformes...
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {scrapers.map((scraper) => {
                  const isSelected = selectedPlatforms.includes(scraper.scraper_name);
                  const isEnabled = scraper.enabled;
                  
                  return (
                    <button
                      key={scraper.scraper_name}
                      onClick={() => isEnabled && onPlatformToggle(scraper.scraper_name)}
                      disabled={!isEnabled}
                      className={cn(
                        "relative p-4 rounded-xl border-2 transition-all text-left",
                        "active:scale-95 min-h-[80px] flex flex-col justify-between",
                        isSelected && isEnabled
                          ? "border-primary bg-primary/10 shadow-md"
                          : "border-border bg-background hover:bg-secondary/50",
                        !isEnabled && "opacity-40 cursor-not-allowed"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className={cn(
                          "text-sm font-semibold leading-tight",
                          isSelected && isEnabled ? "text-primary" : "text-foreground"
                        )}>
                          {scraper.scraper_name}
                        </span>
                        {isSelected && isEnabled && (
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                        {!isEnabled && (
                          <XCircle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      
                      {!isEnabled && (
                        <span className="text-xs text-muted-foreground font-medium mt-2">
                          Temporairement indisponible
                        </span>
                      )}
                      
                      {isEnabled && (
                        <span className={cn(
                          "text-xs font-medium mt-2",
                          isSelected ? "text-primary/70" : "text-muted-foreground"
                        )}>
                          {isSelected ? "Sélectionnée" : "Appuyez pour sélectionner"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Brand Filter */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-base font-bold flex items-center gap-2">
                <Tag className="h-5 w-5 text-primary" />
                Marques ({selectedBrands.length}/{uniqueBrands.length})
              </h4>
              {uniqueBrands.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onSelectAllBrands}
                    disabled={allBrandsSelected}
                    className="text-xs h-8"
                  >
                    Tout
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={onDeselectAllBrands}
                    disabled={selectedBrands.length === 0}
                    className="text-xs h-8"
                  >
                    Aucun
                  </Button>
                </div>
              )}
            </div>

            {uniqueBrands.length === 0 ? (
              <div className="text-center py-8 px-4 bg-muted/30 rounded-xl border-2 border-dashed border-border">
                <Tag className="h-10 w-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-sm text-muted-foreground font-medium">
                  Effectuez une recherche pour voir les marques disponibles
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {uniqueBrands.map((brand) => {
                  const isSelected = selectedBrands.includes(brand);
                  
                  return (
                    <button
                      key={brand}
                      onClick={() => onBrandToggle(brand)}
                      className={cn(
                        "relative p-4 rounded-xl border-2 transition-all text-left",
                        "active:scale-95 min-h-[70px] flex items-center gap-3",
                        isSelected
                          ? "border-primary bg-primary/10 shadow-md"
                          : "border-border bg-background hover:bg-secondary/50"
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="flex-shrink-0"
                      />
                      <span className={cn(
                        "text-sm font-semibold leading-tight",
                        isSelected ? "text-primary" : "text-foreground"
                      )}>
                        {brand}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DrawerFooter className="border-t bg-background">
          <DrawerClose asChild>
            <Button 
              className="w-full h-14 text-base font-semibold"
              disabled={selectedPlatforms.length === 0}
            >
              Appliquer ({selectedPlatforms.length} plateforme{selectedPlatforms.length > 1 ? 's' : ''})
            </Button>
          </DrawerClose>
          <DrawerClose asChild>
            <Button variant="outline" className="w-full h-12">
              Annuler
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileFilterDrawer;
